import React, { useCallback, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomersRequest } from '../../api/customers/customersSlice';
import { debounce } from 'lodash';
import CustomerPopupModal from './CustomerPopupModal';

const CustomerSelect = ({ label = 'Billed to', name, control, errors, readOnly = false, onSelect, setError }) => {
  const dispatch = useDispatch();
  const { customers, loading } = useSelector((state) => state.customers);
  const [searchTerm, setSearchTerm] = useState();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const modifySearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  useEffect(() => {
    dispatch(
      fetchCustomersRequest({
        query: {
          page: 1,
          limit: 10,
          name: searchTerm || undefined,
        },
      }),
    );
  }, [searchTerm, dispatch]);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Autocomplete
              {...field}
              id={name}
              fullWidth
              loading={loading}
              readOnly={readOnly}
              options={customers}
              getOptionLabel={(option) =>
                `${option?.name || '-'} | ${option?.email || '-'} | ${option?.registration_number || '-'}`
              }
              getOptionKey={(option) => option?._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  error={!!errors[name]}
                  helperText={
                    errors[name]?.message ? (
                      <Box sx={{ whiteSpace: 'pre-line', color: 'error.main' }}>{errors[name]?.message}</Box>
                    ) : null
                  }
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => field.onChange(value)}
              value={field?.value || null}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  modifySearchTerm(value);
                }
              }}
              filterOptions={(x) => x}
            />
            {!readOnly && (
              <Button id="add-customer" variant="contained" color="primary" sx={{ py: 1 }} onClick={handleOpen}>
                Add
              </Button>
            )}
          </Box>
        )}
      />
      {open && <CustomerPopupModal open={open} handleClose={handleClose} onSave={onSelect} setError={setError} />}
    </>
  );
};

export default CustomerSelect;
